import React from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'
import type { RecognitionValuesConfig } from '@/types/recognition'
import { RECOGNITION_LIMITS, type RecognitionValuesErrors } from './recognitionUtils'

interface Props {
  config: RecognitionValuesConfig
  onChange: (config: RecognitionValuesConfig) => void
  disabled?: boolean
  errors?: RecognitionValuesErrors
}

/**
 * The community values (F-53): name, description, order and on/off. One
 * component for the creation wizard, the space edit and the wall's "Valores
 * da comunidade" (PADROES-UI section 14). A saved value is never deleted, only
 * turned off; a value not saved yet can be removed.
 */
export const RecognitionValuesFields: React.FC<Props> = ({ config, onChange, disabled = false, errors }) => {
  const { t } = useTranslation('recognition')
  const [newName, setNewName] = React.useState('')
  const values = config?.values ?? []
  const full = values.length >= RECOGNITION_LIMITS.values

  const setValues = (next: RecognitionValuesConfig['values']) => onChange({ ...config, values: next })
  const patch = (index: number, change: Partial<RecognitionValuesConfig['values'][number]>) =>
    setValues(values.map((v, i) => (i === index ? { ...v, ...change } : v)))
  const move = (index: number, delta: -1 | 1) => {
    const target = index + delta
    if (target < 0 || target >= values.length) return
    const next = [...values]
    ;[next[index], next[target]] = [next[target], next[index]]
    setValues(next)
  }
  const add = () => {
    const name = newName.trim()
    if (!name || full) return
    setValues([...values, { name, description: '', active: true }])
    setNewName('')
  }

  return (
    <div className="min-w-0 space-y-2" data-testid="recognition-values">
      <label className="text-sm font-medium text-gray-700">{t('values.label')}</label>
      <p className="text-xs text-gray-500">{t('values.hint')}</p>
      {values.length > 0 && (
        <ul className="space-y-3">
          {values.map((value, index) => (
            <li key={value.id ?? `new-${index}`} className={cn('min-w-0 space-y-2 rounded-lg border border-gray-200 p-3', !value.active && 'bg-gray-50')}>
              <div className="flex min-w-0 items-center gap-2">
                <Input value={value.name} maxLength={RECOGNITION_LIMITS.valueName} disabled={disabled}
                  onChange={(e) => patch(index, { name: e.target.value })}
                  aria-label={t('values.nameLabel', { n: index + 1 })} className="min-w-0 flex-1" />
                <Button type="button" variant="ghost" size="sm" className="h-9 w-9 flex-shrink-0 p-0"
                  disabled={disabled || index === 0} onClick={() => move(index, -1)}
                  aria-label={t('values.moveUp')} title={t('values.moveUp')}>
                  <ArrowUp className="h-4 w-4" />
                </Button>
                <Button type="button" variant="ghost" size="sm" className="h-9 w-9 flex-shrink-0 p-0"
                  disabled={disabled || index === values.length - 1} onClick={() => move(index, 1)}
                  aria-label={t('values.moveDown')} title={t('values.moveDown')}>
                  <ArrowDown className="h-4 w-4" />
                </Button>
                {!value.id && (
                  <Button type="button" variant="ghost" size="sm" className="h-9 w-9 flex-shrink-0 p-0 text-red-600"
                    disabled={disabled} onClick={() => setValues(values.filter((_, i) => i !== index))}
                    aria-label={t('values.remove')} title={t('values.remove')}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
              <Input value={value.description} maxLength={RECOGNITION_LIMITS.valueDescription} disabled={disabled}
                onChange={(e) => patch(index, { description: e.target.value })}
                placeholder={t('values.descriptionPlaceholder')} aria-label={t('values.descriptionLabel', { n: index + 1 })} />
              <div className="flex items-center justify-between gap-3">
                <span className="text-xs text-gray-500">{value.active ? t('values.activeHint') : t('values.inactiveHint')}</span>
                <Switch checked={value.active} disabled={disabled} className="data-[state=checked]:bg-primary"
                  aria-label={t('values.activeLabel', { name: value.name || index + 1 })}
                  onCheckedChange={(checked) => patch(index, { active: checked })} />
              </div>
            </li>
          ))}
        </ul>
      )}
      <div className="flex min-w-0 items-center gap-2">
        <Input value={newName} maxLength={RECOGNITION_LIMITS.valueName} disabled={disabled || full}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add() } }}
          placeholder={t('values.newPlaceholder')} aria-label={t('values.newPlaceholder')} className="min-w-0 flex-1" />
        <Button type="button" variant="outline" size="sm" className="flex-shrink-0 gap-1"
          disabled={disabled || !newName.trim() || full} onClick={add}>
          <Plus className="h-4 w-4" />
          {t('values.add')}
        </Button>
      </div>
      {errors?.values && <p role="alert" className="text-xs text-red-600">{errors.values}</p>}
    </div>
  )
}
